import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Smartphone, X, Download, Plus } from 'lucide-react';
import { haptics } from '../utils/haptics';

/**
 * InstallBanner - A premium UI to guide users to install the PWA.
 */
const InstallBanner = ({ isInstallable, isMobile, onInstall, onDismiss }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showManualGuide, setShowManualGuide] = useState(false);

  useEffect(() => {
    // Show on mobile if not standalone, OR if triggerable
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    if (isMobile && !isStandalone) {
      const timer = setTimeout(() => setIsVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isMobile, isInstallable]);

  if (!isMobile) return null;

  const handleInstallClick = async () => {
    haptics.impact();
    const result = await onInstall();
    if (result === null) {
      // Fallback for HTTP or browsers that don't support beforeinstallprompt
      setShowManualGuide(true);
    }
  };

  const handleDismiss = () => {
    haptics.impact();
    setIsVisible(false);
    if (onDismiss) onDismiss();
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
           initial={{ y: -100, opacity: 0 }}
           animate={{ y: 0, opacity: 1 }}
           exit={{ y: -100, opacity: 0 }}
           className="fixed top-20 md:top-24 left-0 right-0 z-[150] px-4 md:px-6 pointer-events-none"
        >
          <div className="max-w-[500px] mx-auto bg-gray-900/95 dark:bg-white/95 backdrop-blur-2xl rounded-[32px] p-5 shadow-2xl border border-white/10 dark:border-black/5 pointer-events-auto shadow-purple-900/20">
            <div className="flex items-center justify-between gap-4 mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-purple-900/40">
                  <Download size={24} />
                </div>
                <div className="flex flex-col">
                  <span className="text-2xs font-black uppercase tracking-[0.2em] text-primary-600">Premium App</span>
                  <span className="text-sm font-black text-white dark:text-gray-900 leading-tight">Add Opinion to Home Screen</span>
                </div>
              </div>
              <button 
                onClick={handleDismiss}
                className="p-1 text-gray-500 hover:text-white dark:hover:text-gray-900 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {!showManualGuide ? (
              <div className="flex flex-col gap-3">
                <p className="text-2xs text-gray-500 dark:text-gray-500 font-bold italic">Enjoy full-screen, faster speeds, and premium tactile feedback as a native app.</p>
                <button 
                  onClick={handleInstallClick}
                  className="w-full py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 shadow-xl shadow-purple-900/40"
                >
                  <Plus size={16} />
                  Install Engine
                </button>
              </div>
            ) : (
              <div className="bg-white/5 dark:bg-black/5 p-4 rounded-2xl border border-white/5 dark:border-black/5">
                <p className="text-xs font-bold text-gray-300 dark:text-gray-600 mb-2">Instructions for your browser:</p>
                <div className="space-y-2">
                  <div className="flex items-start gap-4 text-[11px] text-gray-500 dark:text-gray-500 font-medium">
                    <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center shrink-0">1</span>
                    <span>Tap the Browser Menu <span className="text-primary-600 font-black">( ⋮ )</span> or Share <span className="text-primary-600 font-black">( ⎙ )</span> icon.</span>
                  </div>
                  <div className="flex items-start gap-4 text-[11px] text-gray-500 dark:text-gray-500 font-medium">
                    <span className="w-5 h-5 bg-primary-600 text-white rounded-full flex items-center justify-center shrink-0">2</span>
                    <span>Select <span className="text-white dark:text-gray-900 font-black italic">"Add to Home Screen"</span>.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InstallBanner;
